/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Florian Hartl <f.hartl@techdivision.com>
 */

// Create a custom theme
import { createTheme, responsiveFontSizes } from '@mui/material';

const colors = {
  primary: '#6bb52c',
  secondary: '#303030',
  text: '#FFF',
  textContrast: '#303030',
  disabled: '#CCC',
  border: '#444',
  gridTable: '#222',
};

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 990,
      lg: 1024,
      xl: 1920,
    },
  },
  palette: {
    background: {
      default: `var(--app-background-color,${colors.secondary})`, // Set the default background color
      gridTable: colors.gridTable,
    },
    primary: {
      main: colors.primary, // Change the primary color
    },
    text: {
      primary: colors.text, // Change the default text color
      secondary: colors.primary, // Color for secondary headlines <Typography color="textSecondary"
    },
    border: {
      default: colors.border,
      active: colors.primary,
    },
  },
  typography: { // ------------------------------------------------ MUI Typography -------------------------------- //
    htmlFontSize: 16, // base font size for the root
    h4: {
      fontSize: '1.5rem', // 1.5 * 16px = 24px
      lineHeight: 1.5,
    },
    h5: {
      fontSize: '1.2rem', // 1.2 * 16px = 19.2px
      lineHeight: 1.6,
    },
  },
  components: {
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          color: colors.textContrast,
          '.MuiSvgIcon-root': {
            fill: colors.textContrast,
          },
          '.MuiTypography-h4': {
            fontSize: '2rem',
          },
        },
      },
    },
    MuiButton: { // ----------------------------------------------- MUI Button --------------------------------- //
      styleOverrides: {
        // for default button
        root: {
          borderRadius: 0,
          '&.Mui-disabled': {
            opacity: 0.3,
            border: `1px solid ${colors.disabled}`,
            color: colors.disabled,
          },
          '&:disabled': {
            opacity: 0.3,
            border: `1px solid ${colors.disabled}`,
            color: colors.disabled,
          },
        }, // Style for contained button
        contained: {
          backgroundColor: colors.primary,
          color: colors.text,
        }, // Style for outlined button
        outlined: {
          backgroundColor: colors.secondary,
          color: colors.text,
          '&:hover': {
            borderColor: colors.primary,
            color: colors.primary,
          },
          '&:focus': {
            borderColor: colors.primary,
            color: colors.primary,
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fill: colors.text,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderColor: colors.text,
        },
        notchedOutline: {
          borderColor: colors.text,
        },
      },
    },
    MuiFormLabel: { // -------------------------------------------- MUI Form Labels -------------------------------- //
      styleOverrides: {
        root: {
          color: colors.text,
          padding: '0 0.25rem',
          backgroundColor: colors.gridTable,
        },
      },
    },
    MuiTextField: { // -------------------------------------------- MUI Checkbox ----------------------------------- //
      styleOverrides: {
        root: {
          '&label.Mui-focused': {
            color: colors.text, // Change the label color when focused
          },
          '&.MuiInput-underline:after': {
            borderBottomColor: colors.text, // Change the underline color when focused
          },
        },
      },
    },
    MuiCheckbox: { // --------------------------------------------- MUI Checkbox ----------------------------------- //
      styleOverrides: {
        root: {
          color: colors.text, // Checkbox color
        },
      },
    },
    MuiRadio: { // ------------------------------------------------ MUI Radio -------------------------------------- //
      styleOverrides: {
        root: {
          color: colors.text, // Radio button color
        },
      },
    },
    MuiSelect: { // ----------------------------------------------- MUI SELECT ------------------------------------- //
      styleOverrides: {
        root: {
          color: colors.text,
          backgroundColor: colors.gridTable,
        },
        outlined: {
          borderColor: colors.text,
        },
        icon: {
          color: colors.text,
          fill: colors.text,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: colors.secondary,
        },
      },
    },
    MuiStepIcon: { // --------------------------------------------- MUI STEPPER  ----------------------------------- //
      styleOverrides: {
        root: {
          '&.Mui-active': {
            fill: colors.primary,
          },
          '&.Mui-completed': {
            fill: colors.primary,
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: colors.text,
          '&.Mui-active': {
            color: colors.primary,
          },
        },
      },
    },
    MuiMobileStepper: {
      styleOverrides: {
        progress: {
          width: '100%',
        },
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          color: colors.textContrast,
          '.MuiSvgIcon-root': {
            fill: colors.textContrast,
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: colors.textContrast,
        },
      },
    },
    MuiAccordion: { // -------------------------------------------- MUI ACCORDION -------------------------------- //
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          padding: 0,
          margin: 0,
          '&.Mui-expanded': {
            margin: 0,
          },
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&.Mui-expanded': {
            minHeight: 'auto',
          },
        },
        content: {
          margin: '1rem 0',
          '&.Mui-expanded': {
            margin: '1rem 0',
          },
        },
      },
    },
  },
});

// ------------------------- RESPONSIVE OVERRIDES ---------------- //

const responsiveTheme = responsiveFontSizes(theme);

responsiveTheme.typography.h1 = {
  fontSize: '3rem', // 3 * 16px = 48px
  lineHeight: 1.15,
  color: colors.primary,
  letterSpacing: '3px',
  wordBreak: 'break-word',
  maxWidth: '500px',
  [theme.breakpoints.down('md')]: {
    fontSize: '2rem', // 2 * 16px = 32px
    letterSpacing: '2px',
  },
};
responsiveTheme.typography.h2 = {
  fontSize: '2.2rem', //  2.2 * 16px = 35.2px
  lineHeight: 1.3,
  marginBottom: '1rem',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.75rem', // 28px
  },
};
responsiveTheme.typography.h3 = {
  fontSize: '1.8rem', // 1.8 * 16px = 28.8px
  lineHeight: 1.4,
  marginBottom: '1rem',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.5rem', // 24px
  },
};

export default responsiveTheme;
